import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of OutletStatusSlice — plan §5.2.
 *
 * The cleanest split so far: **zero divergent reducers**. All 22 of mobile's reducers are
 * identical to web's and live here; web adds 7 of its own (branch toggling + the info-modal
 * cluster), 3 state fields, and the logout `extraReducers`.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload. Reducers the apps wrote with no `action` param at all keep
 * that shape: RTK generates a no-payload creator for them today, which is the behaviour to
 * preserve.
 *
 * `allOutlets` / `lastActivitiesPerOutlet` are typed as objects but initialised to `[]` in
 * BOTH apps, and `clearAllResponsesFromOutlet` resets the latter to `{}`. Preserved verbatim
 * — it typechecks, consumers already cope, and "fixing" it is a behaviour change.
 */
export interface SharedOutletState {
  allOutlets: {
    [key: string]: any;
  };
  lastActivitiesPerOutlet: {
    [key: string]: any;
  };
  toggleOutletResponse: {
    [key: string]: any;
  };
  outletStatus: any;
  activityError: any;
  bulkOutletToggleResponse: any;
  outletDeliveryStatus: any;
  updateOutletDeliveryStatusResponse: any;
  helpResponse: any;
  branchData: any;
}

export const sharedInitialState: SharedOutletState = {
  allOutlets: [],
  lastActivitiesPerOutlet: [],
  toggleOutletResponse: {},
  outletStatus: {},
  activityError: {},
  bulkOutletToggleResponse: {},
  outletDeliveryStatus: {},
  updateOutletDeliveryStatusResponse: {},
  helpResponse: {},
  branchData: {},
};

/** Identical in both apps — every mobile reducer is here. */
export const sharedReducers = {
  // No `action` param in either app — RTK generates a no-payload creator. Keep it that way.
  getAllOutlets: (state: SharedOutletState) => {},
  getAllOutletsSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.allOutlets = action.payload;
  },
  getAllActivitiesPerOutlet: (state: SharedOutletState, action: PayloadAction<any>) => {},
  getAllActivitiesPerOutletSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.lastActivitiesPerOutlet = action.payload;
  },
  toggleOutlet: (state: SharedOutletState, action: PayloadAction<any>) => {},
  toggleOutletSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.toggleOutletResponse = action.payload;
  },
  fetchOutletStatus: (state: SharedOutletState, action: PayloadAction<any>) => {},
  fetchOutletStatusSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.outletStatus = action.payload;
  },
  fetchActivityError: (state: SharedOutletState, action: PayloadAction<any>) => {},
  fetchActivityErrorSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.activityError = action.payload;
  },
  clearToggleResponse: (state: SharedOutletState) => {
    state.toggleOutletResponse = {};
  },
  bulkOutletToggle: (state: SharedOutletState, action: PayloadAction<any>) => {},
  bulkOutletToggleSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.bulkOutletToggleResponse = action.payload;
  },
  clearBulkToggleResponse: (state: SharedOutletState) => {
    state.bulkOutletToggleResponse = {};
  },
  getOutletDeliveryStatus: (state: SharedOutletState, action: PayloadAction<any>) => {},
  getOutletDeliveryStatusSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.outletDeliveryStatus = action.payload;
  },
  updateOutletDeliveryStatus: (state: SharedOutletState, action: PayloadAction<any>) => {},
  updateOutletDeliveryStatusSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.updateOutletDeliveryStatusResponse = action.payload;
  },
  getHelpLink: (state: SharedOutletState, action: PayloadAction<any>) => {},
  getHelpLinkSuccess: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.helpResponse = action.payload;
  },
  clearAllResponsesFromOutlet: (state: SharedOutletState) => {
    state.toggleOutletResponse = {};
    state.bulkOutletToggleResponse = {};
    state.updateOutletDeliveryStatusResponse = {};
    state.lastActivitiesPerOutlet = {};
  },
  clearHelpLink: (state: SharedOutletState) => {
    state.helpResponse = {};
  },
  // No `action` param — matches the shape web has always had, so its generated
  // creator is unchanged (it is dispatched with a branchId from JS callers).
  getBranchData: (state: SharedOutletState) => {},
  storeBranchData: (state: SharedOutletState, action: PayloadAction<any>) => {
    state.branchData = action.payload;
  },
  branchDataCallComplete: (state: SharedOutletState) => {
    // this action is to tell that the branches data call has completed
  },
};
